import { Component, createRef, Fragment } from 'react';

import { IOC_TYPES } from '../../../context/ioc.types';
import { lazyInject } from '../../../context/module';
import { Logger } from '../../../models/logger';
import { EnvironmentService } from '../../../services/environment.service';
import { MotivesService } from '../../../services/motives.service';
import { WindowService } from '../../../services/window.service';
import { get, isNaN, isNumber, parseInt, toString } from '../../../utils/lodash-min';
import { reloadAfterExecute } from '../../../utils/react.utils';
import { MotiveConfig, MotiveLocationType } from '../../../utils/request.utils';
import { StringUtils } from '../../../utils/string-utils';
import { untilDestroyed } from '../../../utils/until-destroyed';
import styles from './motive.component.module.scss';
import { canRequestAdSlot } from './ad-slot.util';
import { AD_CLIENT } from './ad-slots';
// import FreestarAdSlot from '@freestar/pubfig-adslot-react-component'
import { WindowSize, WindowUtils } from '../../../utils/window.utils';
import { BrowserUtils } from '../../../ui-utils/browser-utils';


const defaultOptions = {
    showAds: true,
    className: 'width_major',
}

interface MotiveComponentState {
    showAds?: boolean;
    /**
     * The user closed this ad themselves, as opposed to the slot never being enabled. Only the
     * first case earns an upgrade offer -- it is the one moment we know they do not want ads.
     */
    dismissedByUser?: boolean;
    styles: { [key: string]: string }
}

interface MotiveComponentProps {
    children?: any;
    showAds?: boolean;
    data?;
    containerWidth?: string;
    containerHeight?: string;
    adWidth?: string;
    adHeight?: string;
    className?: 'width_major' | 'height_major' | 'width_major_md' | 'rect_med';
    location?: MotiveLocationType;
}

export class MotiveComponent extends Component<MotiveComponentProps, MotiveComponentState> {
    logger = Logger.getLogger('MotiveComponent');

    mergedOptions: MotiveComponentProps = {} as any;
    currentMotiveSlotIsEnabled = false;
    motiveType = null;
    uuid = 'x' + (StringUtils.uuid() as string).replace(/\-/g, '');
    initialized = false;
    private effectiveMotives: MotiveConfig;

    motiveLocationDisplayedAfterUserInteration = true;
    userInteractedWithPage = false;

    // Preact fix
    // @ViewChild('parent') parent: ElementRef;
    // @ViewChild('container') container: ElementRef;
    parent = createRef<HTMLDivElement>();
    container = createRef<HTMLDivElement>();

    publisher = 'xconvert-com'
    placementName;
    slotId;
    targeting = {};

    /*** This is no longer used by the shouldShowAds method */
    get meetsUserInteractionCondition() {
        return !this.motiveLocationDisplayedAfterUserInteration || (this.motiveLocationDisplayedAfterUserInteration && this.userInteractedWithPage);
    }

    get shouldShowAds() {
        return this.state.showAds && (this.currentMotiveSlotIsEnabled || false) && (this.meetsUserInteractionCondition || true);
    }

    @lazyInject(MotivesService) private motivesService: MotivesService;
    @lazyInject(WindowService) private windowService: WindowService;
    @lazyInject(EnvironmentService) private environmentService: EnvironmentService;

    readonly state: Readonly<MotiveComponentState> = { showAds: this.props.showAds, styles: {} }
    static counter = 1;
    constructor(props: MotiveComponentProps) {
        super(props);
        this.slotId = 'in_content_ad_' + (MotiveComponent.counter++);

        if (this.props.location.match(/left/i)) {
            this.placementName = `xconvert_sidewall_left`
        } else if (this.props.location.match(/right/i)) {
            this.placementName = `xconvert_sidewall_right`
        } else if (this.props.location.match(/top/i) || this.props.location.match(/title/i)) {
            this.placementName = `xconvert_header`
        } else if (this.props.location.match(/bottom/i)) {
            this.placementName = `xconvert_footer`
        }
    }

    private selectValueBasedOnWindowWidth(sm, md, lg) {
        if (!BrowserUtils.hasWindow()) {
            return lg;
        }
        if (WindowUtils.getWindowSize() <= WindowSize.SM) {
            return sm
        }
        if (WindowUtils.getWindowSize() <= WindowSize.MD) {
            return md
        }
        return lg
    }

    componentDidMount() {
        this.windowService.userEngaged.subscribe({
            next: () => {
                this.userInteractedWithPage = true;
                this.updateView();
            }
        });

        this.motivesService.onEffectiveMotiveSettingsChange.pipe(untilDestroyed(this)).subscribe({
            next: (motives) => {
                this.effectiveMotives = motives;
                this.motiveLocationDisplayedAfterUserInteration = !(this.effectiveMotives.bypassMouseEvents || []).includes(this.mergedOptions.location);
                this.setUpMotiveRotation();
                this.updateView();
            },
            error: (error) => this.logger.error(error),
        });

        // if (this.container && this.container.nativeElement) {
        //     /* tslint:disable */
        //     const flags = RendererStyleFlags2.DashCase | RendererStyleFlags2.Important;
        //     // this.renderer.setStyle(this.container.nativeElement, 'margin', 'auto');
        //     // this.renderer.setStyle(this.parent.nativeElement, 'width', this.containerWidth, flags);
        //     // this.renderer.setStyle(this.parent.nativeElement, 'height', this.containerHeight, flags);
        // }

        if (BrowserUtils.hasWindow()) {
            if (this.props.location.match(/left/i)) {
                this.setState({
                    styles: { display: this.selectValueBasedOnWindowWidth('none', 'block', 'block') }
                })

            } else if (this.props.location.match(/right/i)) {
                this.setState({
                    styles: { display: this.selectValueBasedOnWindowWidth('none', 'block', 'block') }
                })
            } else if (this.props.location.match(/top/i) || this.props.location.match(/title/i) || this.props.location.match(/bottom/i)) {
                this.setState({
                    styles: {
                        display: this.selectValueBasedOnWindowWidth('block', 'block', 'block'),
                        height: this.selectValueBasedOnWindowWidth('120px', '120px', '120px'),
                        maxHeight: this.selectValueBasedOnWindowWidth('120px', '120px', '120px')
                    }
                })
            }
        }
        this.initialized = true;
    }

    /**
     * Automatic ad refresh is deliberately not implemented.
     *
     * The settings document still carries `rotateMotives` (prod: true, every 125s +/- 35s) from
     * when this component last rendered anything. Honouring it now would refresh the ad several
     * times per visit, because a converter page holds the user while their job runs. Refreshed
     * impressions are low-viewability -- they drag RPM down on the slots that remain -- and a
     * short forced cycle is the pattern that gets an AdSense account looked at. The account is
     * the revenue channel, so it is worth more than the impressions a timer would add.
     *
     * The setting is left in the schema rather than removed so nothing else reading the
     * settings document breaks. See docs/proposed/AD_PLACEMENT_DESIGN.md 4.3.
     */
    setUpMotiveRotation() {
        // intentionally empty
    }

    @reloadAfterExecute()
    updateView() {
        if (this.effectiveMotives) {
            this.motiveType = this.effectiveMotives && this.effectiveMotives.externalMotiveType;
        }

        if (this.mergedOptions.showAds) {
            this.currentMotiveSlotIsEnabled = !!get(this.effectiveMotives, this.mergedOptions.location);
            if (this.currentMotiveSlotIsEnabled) {
                this.runMotives();
                this.updateWidth();
            }
        }
    }


    shouldComponentUpdate(nextProp: MotiveComponentProps) {
        // Was `nextProp.data !== nextProp.data`, which is always false, so no parent re-render
        // ever reached this component -- and because React skips componentDidUpdate when
        // shouldComponentUpdate returns false, the showAds prop handler below was dead too.
        // Harmless while render() returned an empty span; not harmless now.
        return nextProp.data !== this.props.data || nextProp.showAds !== this.props.showAds;
    }

    componentDidUpdate(prevProps: MotiveComponentProps) {
        if (prevProps.showAds != this.props.showAds) {
            this.setState({
                showAds: this.props.showAds,
            });
            this.updateView();
        }
    }

    private updateWidth() {
        const widthAsNumber = parseInt(this.mergedOptions.adWidth);
        if (isNumber(widthAsNumber) && !isNaN(widthAsNumber)) {
            try {
                if (window && (window.innerWidth < widthAsNumber)) {
                    this.mergedOptions.adWidth = toString(window.innerWidth) + 'px';
                }
            } catch {
                // prevent throw in SSR mode
            }
        }
    }

    runMotives() {
        if (this.motiveType === 'buysell') {
            //  <script *ngIf="motiveType == 'buysell'" async type="text/javascript" src="//cdn.carbonads.com/carbon.js?serve=CE7DC27L&placement=wwwxconvertcom" id="_carbonads_js"></script>

            const node = document.createElement('script');
            node.src = "//cdn.carbonads.com/carbon.js?serve=CE7DC27L&placement=wwwxconvertcom";
            node.type = 'text/javascript';
            node.async = true;
            node.id = this.uuid + "_carbonads_js",
                node.onload = () => {

                };
            setTimeout(() => {
                document.getElementById(this.uuid).appendChild(node);
            }, 100);
            return;
        }
        setTimeout(() => {
            try {
                const viewWidth = window && window.innerWidth;
                if (this.shouldFitToContainer()) {
                    // TODO Preact see that angular does
                    // const rect = this.containerElemRef.current.getBoundingClientRect();
                    // this.renderer.setStyle(this.containerElemRef.nativeElement, 'height', '90px');
                    // this.renderer.setStyle(this.containerElemRef.nativeElement, 'width', rect.width + 'px');
                    // this.renderer.setStyle(this.parent.nativeElement, 'width', rect.width + 'px');
                }
                // Only request an ad when this slot exists and hasn't been filled yet.
                // Re-pushing an already-filled <ins> makes AdSense throw a benign
                // "already have ads" TagError; runMotives() re-runs often (engagement,
                // settings changes, rotation, prop updates), so without this guard that
                // error floods our logs hundreds of thousands of times a day.
                if (!canRequestAdSlot(this.container.current)) {
                    return;
                }
                (window['adsbygoogle'] = window['adsbygoogle'] || []).push({});
            } catch (e) {
                // A residual benign TagError can still slip through under races. It is
                // expected and non-actionable, so log at debug (not forwarded to the
                // backend in prod) rather than error to keep it out of our logs.
                this.logger.debug('adsbygoogle push skipped: ' + (e && e.message));
            }
        }, 400);
    }


    shouldFitToContainer() {
        return this.mergedOptions.containerWidth === '100%';
    }

    toggleGContent() {
        this.setState({
            showAds: !this.state.showAds,
            dismissedByUser: this.state.showAds,
        });
        this.updateView();
    }

    showGContent() {
        this.setState({
            showAds: true,
        });
        this.updateView();
    }

    getMotiveOrPlaceholder() {
        if (this.shouldShowAds) {
            return <div className={`m-container ${styles['external-motives']}`} ref={this.parent}>
                <div ref={this.container} className={`gcontainer ${styles['gcontainer']} ${styles[this.props.className]}`}>
                    {
                        this.motiveType == 'google' && <ins className={`adsbygoogle ${styles[this.props.className]}`}
                            style={{ display: 'inline-block', textAlign: 'center' }} data-ad-client={AD_CLIENT}
                            data-ad-slot={this.mergedOptions.data}></ins>
                    }
                </div>
                <div className={`gclose`}
                    style={{
                        position: 'absolute',
                        right: 0,
                        top: 0,
                        fontSize: '.7rem',
                        padding: '.5rem',
                        background: '#717171',
                        borderRadius: '100%',
                        color: '#fff',
                        lineHeight: '.5rem',
                        cursor: 'pointer',
                        zIndex: 1,
                    }}
                    onClick={(e) => this.toggleGContent()}>X</div>
            </div>
        }
        return <div className={`m-container ${styles['m-container']} ${styles['external-motives']}`}>
            <div className={`no-m gcontainer ${styles['gcontainer']} ${styles[this.props.className]}`}>
                {
                    /*
                     * Closing an ad is the one moment we know for certain the user does not want
                     * ads, so it is where the ad-free offer belongs -- not only on the pricing
                     * page they have no reason to visit. It renders inside the same reserved
                     * box the ad occupied, so nothing on the page moves.
                     *
                     * Only after a real dismissal: a slot that was never enabled (every paid
                     * user, every disabled location) keeps the silent placeholder, because
                     * selling ad-free to someone who already has it is noise.
                     *
                     * docs/proposed/RETENTION_FIXES.md T4.5
                     */
                    this.state.dismissedByUser
                        ? <div className={styles['upgrade-offer']}>
                            Ads keep XConvert free.{' '}
                            {/* /pricing does not exist (404). The copy names one specific
                                plan, so link straight to it -- owner call 2026-09-09. */}
                            <a href="/product/backer_monthly">Go ad-free from $4.99/month</a>
                        </div>
                        : <div></div>
                }
            </div>
        </div>
    }

    // getFreestarMotiveOrPlaceholder() {
    //     if (!this.placementName || !this.shouldShowAds) {
    //         return <div  className={`${(this.placementName && styles[this.placementName]) || 'empty hidden'}`}>
    //         </div>
    //     }
    //     return <>
    //         <div className={`${(this.placementName && styles[this.placementName]) || ''}`} style={{
    //             ...this.state.styles,
    //             alignItems: 'flex-start',
    //             display: 'flex',
    //             justifyContent: 'flex-start'
    //         }}>
    //             {
    //             <FreestarAdSlot
    //                 publisher={this.publisher}
    //                 placementName={this.placementName}
    //                 slotId={`${this.props.location}`}
    //                 targeting={this.targeting}
    //                 channel='custom_channel'
    //                 classList={[]}
    //             />
    //             }
    //         </div>
    //     </>
    // }

    render() {
        this.mergedOptions = Object.assign({}, defaultOptions, this.props);
        // return this.getFreestarMotiveOrPlaceholder();
        return this.getMotiveOrPlaceholder();
    }
}
